// owner-map: REG-34
/**
 * The chatboard API's route templates, as `net.request` records them (services/netCapture.js):
 * a request path becomes its template, every segment that is not a static path word of the API
 * becoming `:id`. Dependency-free so the replay scripts (scripts/replay) read the same
 * vocabulary to tell a recorded route from user text.
 */

// gen-route-table:begin
/**
 * The static path words of the chatboard API — generated by `node scripts/gen-route-table.mjs`
 * from `createApp(...).routes` (`server/app/registry.js`) plus the generator's EXTRA_SEGMENTS
 * (words no registered route carries but recorded routes still use); `scripts/check.sh
 * --step route-table` fails when this set is stale. Any other segment — an id, a series name
 * the user typed, a token — is templated to `:id`, whatever its shape, so a route never
 * carries input; an unknown static word costs only detail.
 */
export const STATIC_SEGMENTS = new Set([
  ".well-known",
  "access",
  "account",
  "active",
  "alternate",
  "api",
  "archive",
  "assets",
  "auth",
  "authorize",
  "auto",
  "branch",
  "branch-triggers",
  "branding",
  "bug-report",
  "by-contract",
  "by-id",
  "cadence",
  "calibration",
  "callback",
  "cancel",
  "chat",
  "chat-turn",
  "claim",
  "clear",
  "connect",
  "connections",
  "contracts",
  "convert",
  "copy",
  "copy-links",
  "csp-report",
  "decision",
  "derived-branch",
  "dynamic",
  "events",
  "forecast",
  "forecast-events",
  "forecasts",
  "health",
  "html",
  "interests",
  "internal",
  "listen",
  "llm",
  "local",
  "login",
  "logout",
  "mcp",
  "me",
  "memory",
  "messages",
  "multi",
  "notifier-version",
  "notify",
  "oauth",
  "oauth-authorization-server",
  "oauth-protected-resource",
  "preferences",
  "premise",
  "preprocess",
  "preview",
  "profile",
  "redeem",
  "report",
  "reports",
  "resolve",
  "result",
  "revoke",
  "run",
  "sayer-forecasts",
  "score",
  "series",
  "series-resolve",
  "share-candidates",
  "shared",
  "shares",
  "signals",
  "simulate-lifecycle",
  "sooth_favicon.ico",
  "sooth_favicon_64.png",
  "status",
  "stream",
  "suggest",
  "sweep",
  "tag",
  "telemetry",
  "token",
  "tos-accept",
  "triggers",
  "unsubscribe",
  "user-events",
  "variants",
  "version",
  "watchlist",
  "zitadel",
]);
// gen-route-table:end

/** Literal routes under /api/forecasts whose second segment is not an id. */
const FORECASTS_LITERALS = new Set(["active", "by-contract", "by-id", "shared"]);

/** A segment decoded, or as is when its percent-encoding is malformed. */
function safeDecode(seg) {
  try {
    return decodeURIComponent(seg);
  } catch {
    return seg;
  }
}

/** A URL (absolute or rooted) as its route template: path only, never a query string. */
export function routeOf(url) {
  try {
    return routeTemplate(new URL(url, "http://x.invalid").pathname);
  } catch {
    return null;
  }
}

/** `/api/forecasts/<uuid>/messages` → `/api/forecasts/:id/messages`; see STATIC_SEGMENTS. */
export function routeTemplate(pathname) {
  const segs = pathname
    .split("/")
    .map((seg) => (seg === "" || STATIC_SEGMENTS.has(safeDecode(seg)) ? seg : ":id"));
  // Known parameter positions win over the word allowlist: a series name the user
  // typed may happen to equal a path word (`/api/series/events`).
  const param = (i) => {
    if (i < segs.length && segs[i] !== "") segs[i] = ":id";
  };
  const [, api, area, second, third] = segs;
  if (api === "api") {
    if (area === "series" || area === "copy-links") param(3);
    if (area === "forecasts") {
      if (second === "run") param(4);
      else if (!FORECASTS_LITERALS.has(second)) param(3);
      if (third === "resolve") param(5);
    }
  }
  return segs.join("/");
}
